import {DataCollaborator, OwnerInfo} from 'auth/PermissionConstants';
import {GatewayType} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {ConnectionListingGraphQL} from '../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {AdapterField, InstrumentAdapter, InstrumentServiceDetails} from './ConnectionConstants';

export function connectionURL(connectionId: string): string {
  return `/connections/${connectionId}`;
}

export function createConnection(args: {
  schemaId?: string;
  adapterId?: string;
  organizationId?: string;
  name: string;
  tags: TagUpdateObject;
  gatewayId: string;
  gatewayType: GatewayType;
  dataCatalogFolderId: string | null;
  equipmentIds: Array<string>;
  analysisTemplateIds?: Array<string>;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: '/1/api/connections',
    data: args,
  });
}

export function updateConnection(
  connectionId: string,
  data: {
    name?: string;
    tags?: TagUpdateObject;
    gatewayType?: GatewayType;
    dataCatalogFolderId?: string;
    isEnabled?: boolean;
    equipmentIds?: Array<string>;
    analysisTemplateIds?: Array<string>;
  }
): Promise<{id: string}> {
  return net.patchJSONPromise({
    url: `/1/api/connections/${connectionId}`,
    data,
  });
}

export function listConnections(adapterId?: string): Promise<{connections: Array<ConnectionListingGraphQL>}> {
  return net.getJSONPromise('/1/api/connections', adapterId ? {adapterId} : {});
}

export function updateConnectionOwnableInfo(
  connectionId: string,
  data: {
    collaborators: Array<DataCollaborator>;
    ownerInfo: OwnerInfo;
    owner: string;
  }
): Promise<{}> {
  return net.patchJSONPromise({
    url: `/1/api/connections/${connectionId}/collaborators`,
    data,
  });
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<BulkArchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/connections:bulk-archive',
    data: {ids, purpose},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchive(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/connections:bulk-unarchive',
    data: {ids},
  });
}

// Exports a Snowflake table through the connection into a new Benchling Dataset.
// Dispatches an async task (the export can take minutes) and resolves once the task
// completes with the created dataset's id.
export function materializeSnowflakeTable(
  connectionId: string,
  schemaName: string,
  tableName: string,
  datasetName: string
): Promise<{dataset_id: string}> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/instruments/connections/${connectionId}:materialize-table`,
    data: {schemaName, tableName, datasetName},
  });
}

export function listInstrumentTypes(): Promise<{instrumentAdapters: Array<InstrumentAdapter>}> {
  return net.getJSONPromise(`/1/api/instruments/instrument_types`);
}

export function listAdapters(
  instrumentType?: string
): Promise<{instrumentAdapters: Array<InstrumentAdapter>}> {
  return net.getJSONPromise(`/1/api/instruments/adapters?instrumentType=${instrumentType}`);
}

export function getAdapter(
  adapterId: string,
  gatewayType?: GatewayType
): Promise<{id: string; fields: Array<AdapterField>}> {
  const data = gatewayType ? {gatewayType} : {};
  return net.getJSONPromise(`/1/api/instruments/adapters/${adapterId}`, data);
}

export function getTransformDataTypes(adapterId: string): Promise<{[id: string]: string}> {
  return net.getJSONPromise(`/1/api/instruments/transform-data-types`, {adapterId});
}

export function getInstrumentServiceDetails(connectionId: string): Promise<InstrumentServiceDetails> {
  return net.getJSONPromise(`/1/api/connections/${connectionId}/info`);
}

export function startCredentialTest(connectionId: string): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: `/1/api/connections/${connectionId}:test-credentials`,
    data: {},
  });
}
